/**
 * Primitives de skeleton loading. Substituem os `<Loader2 animate-spin/>`
 * centrados quando faz mais sentido mostrar um placeholder do layout final
 * — o usuário percebe a estrutura enquanto os dados chegam.
 */

interface SkeletonProps {
  className?: string;
}

/**
 * Bloco skeleton individual. Compose livremente com Tailwind:
 *   <Skeleton className="h-4 w-24" />
 *   <Skeleton className="h-10 w-full rounded-md" />
 */
export function Skeleton({ className = '' }: SkeletonProps) {
  return <div className={`animate-pulse rounded bg-gray-200 ${className}`.trim()} aria-hidden />;
}

interface SkeletonRowProps {
  cols: number;
  className?: string;
}

/**
 * Linha de tabela com N células, cada uma contendo um `<Skeleton>`.
 * Use como filho direto de `<tbody>` durante o loading da listagem:
 *   {isLoading && Array.from({length: 5}).map((_, i) => (
 *     <SkeletonRow key={i} cols={7} />
 *   ))}
 */
export function SkeletonRow({ cols, className = '' }: SkeletonRowProps) {
  return (
    <tr className={className}>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton className="h-4 w-full" />
        </td>
      ))}
    </tr>
  );
}

interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

/**
 * Bloco de N linhas de texto skeleton (largura decrescente na última linha
 * para simular parágrafo real). Útil dentro de cards ou detail views.
 */
export function SkeletonText({ lines = 3, className = '' }: SkeletonTextProps) {
  return (
    <div className={`space-y-2 ${className}`.trim()}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={`h-3 ${i === lines - 1 ? 'w-2/3' : 'w-full'}`} />
      ))}
    </div>
  );
}
